// Axios API client
import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 30000,
});

// Request interceptor (attach token if available)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Endpoints that are allowed to return 401 without forcing a redirect.
// (auth/me is a session probe; orders/counts polls in the background.)
const SILENT_401_ENDPOINTS = ['/auth/me', '/orders/counts'];

// Routes that are public — never redirect to /login from here.
const PUBLIC_ROUTE_PATTERNS = [
  /^\/$/,            // landing
  /^\/m\//,          // public menu
  /^\/login$/,
  /^\/register$/,
];

// Response interceptor (handle errors globally)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const path = window.location.pathname;
      const isSilent = SILENT_401_ENDPOINTS.some(e => url.includes(e));
      const onPublicRoute = PUBLIC_ROUTE_PATTERNS.some(re => re.test(path));

      // Always drop the stale token, but only redirect on protected pages
      // when the failing call wasn't a silent probe.
      localStorage.removeItem('token');
      if (!isSilent && !onPublicRoute) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data) => api.post('/auth/register', data).then(r => r.data),
  login: (data) => api.post('/auth/login', data).then(r => r.data),
  logout: () => api.post('/auth/logout').then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  updateProfile: (data) => api.patch('/auth/me', data).then(r => r.data.user),
  changeEmail: (data) => api.post('/auth/email', data).then(r => r.data.user),
  changePassword: (data) => api.post('/auth/password', data).then(r => r.data),
  deleteAccount: (data) => api.delete('/auth/me', { data }).then(r => r.data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }).then(r => r.data),
  resetPassword: (data) => api.post('/auth/reset-password', data).then(r => r.data),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }).then(r => r.data),
  sendVerification: () => api.post('/auth/send-verification').then(r => r.data),
  dismissOnboarding: () => api.post('/auth/onboarding/dismiss').then(r => r.data),
};

// Menus API
export const menusApi = {
  list: () => api.get('/menus').then(r => r.data.menus),
  get: (id) => api.get(`/menus/${id}`).then(r => r.data.menu),
  getFull: (id) => api.get(`/menus/${id}/full`).then(r => r.data),
  // AI extraction (PRO) — accepts a PDF or one/many images
  extractFromPdf: (id, files) => {
    const fd = new FormData();
    const list = Array.isArray(files) ? files : [files];
    list.forEach(f => fd.append('files', f));
    return api.post(`/menus/${id}/extract-pdf`, fd, {
      timeout: 120000,
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  applyExtracted: (id, payload) =>
    api.post(`/menus/${id}/apply-extracted`, payload).then(r => r.data),
  create: (data) => api.post('/menus', data).then(r => r.data.menu),
  update: (id, data) => api.patch(`/menus/${id}`, data).then(r => r.data.menu),
  delete: (id) => api.delete(`/menus/${id}`).then(r => r.data),
  uploadPdf: (id, file) => {
    const fd = new FormData();
    fd.append('pdf', file);
    return api.post(`/uploads/menus/${id}/pdf`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deletePdf: (id) => api.delete(`/uploads/menus/${id}/pdf`).then(r => r.data),
  uploadPdfPage: (id, blob) => {
    const fd = new FormData();
    fd.append('image', blob, 'page.png');
    return api.post(`/uploads/menus/${id}/pdf-page`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data.url);
  },
  setPdfPages: (id, pages) =>
    api.patch(`/uploads/menus/${id}/pdf-pages`, { pages }).then(r => r.data.menu),
};

// Sections API
export const sectionsApi = {
  list: (menuId) => api.get(`/sections?menuId=${menuId}`).then(r => r.data.sections),
  create: (data) => api.post('/sections', data).then(r => r.data.section),
  update: (id, data) => api.patch(`/sections/${id}`, data).then(r => r.data.section),
  delete: (id) => api.delete(`/sections/${id}`).then(r => r.data),
  reorder: (menuId, sectionIds) => api.post('/sections/reorder', { menuId, sectionIds }).then(r => r.data),
};

// Items API
export const itemsApi = {
  list: (sectionId) => api.get(`/items?sectionId=${sectionId}`).then(r => r.data.items),
  create: (data) => api.post('/items', data).then(r => r.data.item),
  update: (id, data) => api.patch(`/items/${id}`, data).then(r => r.data.item),
  delete: (id) => api.delete(`/items/${id}`).then(r => r.data),
  duplicate: (id) => api.post(`/items/${id}/duplicate`).then(r => r.data.item),
  reorder: (sectionId, itemIds) => api.post('/items/reorder', { sectionId, itemIds }).then(r => r.data),
  generateImage: (id, extraStyle = '') => api.post(`/items/${id}/generate-image`, { extraStyle }, { timeout: 60000 }).then(r => r.data),
  searchImages: (id, query = '') => api.get(`/items/${id}/search-images`, { params: { q: query } }).then(r => r.data),
  useImage: (id, url) => api.post(`/items/${id}/use-image`, { url }).then(r => r.data),
};

// Analytics API
export const analyticsApi = {
  overview: () => api.get('/analytics/overview').then(r => r.data),
  forMenu: (menuId) => api.get(`/analytics/menus/${menuId}`).then(r => r.data),
};

// Orders API
export const ordersApi = {
  list: (params = {}) => api.get('/orders', { params }).then(r => r.data.orders),
  counts: () => api.get('/orders/counts').then(r => r.data.counts),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }).then(r => r.data),
};

// Billing / Paddle (Billing API)
export const billingApi = {
  plans: () => api.get('/billing/plans').then(r => r.data),
  subscription: () => api.get('/billing/subscription').then(r => r.data.subscription),
  checkout: (priceId) => api.post('/billing/checkout', { priceId }).then(r => r.data),
  cancel: () => api.post('/billing/cancel').then(r => r.data),
};

// Admin API (platform admin only)
export const adminApi = {
  aiUsage: () => api.get('/admin/ai-usage').then(r => r.data),
  emails: (params = {}) => api.get('/admin/emails', { params }).then(r => r.data),
  paidUsers: () => api.get('/admin/paid-users').then(r => r.data),
  monthlyReportPreview: () => api.get('/admin/monthly-report').then(r => r.data),
  // Bulk send can take a couple of minutes for dozens of users (sequential
  // SMTP with provider rate-limits), so override the default 30s timeout.
  monthlyReportSend: (payload) =>
    api.post('/admin/monthly-report/send', payload, { timeout: 300000 }).then(r => r.data),
  stats: () => api.get('/admin/stats').then(r => r.data),
  users: (params = {}) => api.get('/admin/users', { params }).then(r => r.data),
  menus: (params = {}) => api.get('/admin/menus', { params }).then(r => r.data),
  impersonate: (userId) => api.post(`/admin/impersonate/${userId}`).then(r => r.data),
};

// Public API (no auth)
export const publicApi = {
  getMenu: (slug, visitorId) => api.get(`/public/m/${slug}`, {
    params: visitorId ? { vid: visitorId } : undefined,
  }).then(r => r.data.menu),
  trackScan: (slug) => api.post(`/public/m/${slug}/scan`).then(r => r.data),
  toggleLike: (itemId, visitorId) =>
    api.post(`/public/items/${itemId}/like`, { visitorId }).then(r => r.data),
  placeOrder: (slug, orderData) =>
    api.post(`/public/m/${slug}/orders`, orderData).then(r => r.data),
};

export default api;
